import RemoveIcon from '@mui/icons-material/Remove';
import {
  Box,
  InputAdornment,
  OutlinedInput,
  Slider,
  Typography,
} from '@mui/material';
import {SliderThumb} from '@mui/material/Slider';
import {styled} from '@mui/material/styles';
import PropTypes from 'prop-types';
import React, {useEffect, useState} from 'react';
import {useTranslation} from 'react-i18next';

const NumberField = ({unit, min, max, step, ...props}) => {
  const {t} = useTranslation();
  return (
    <OutlinedInput
      fullWidth
      size="small"
      margin="dense"
      variant="outlined"
      type="number"
      inputProps={{
        min: min,
        max: max,
        step: step,
        sx: {
          paddingY: 1,
        },
      }}
      endAdornment={<InputAdornment position="end">{t(unit)}</InputAdornment>}
      {...props}
    />
  );
};

NumberField.propTypes = {
  unit: PropTypes.string,
  min: PropTypes.number,
  max: PropTypes.number,
  step: PropTypes.number,
};

const StyledSlider = styled(Slider)(({theme}) => ({
  height: 2,
  margin: theme.spacing(0, 1.5),
  '& .MuiSlider-thumb': {
    height: 27,
    width: 27,
    backgroundColor: theme.vars.palette.background.paper,
    border: '1px solid currentColor',
    boxShadow: '0 1px 1px 0 rgba(0,0,0,0.32)',
    '&:hover': {
      boxShadow: `0px 0px 0px 8px rgba(${theme.vars.palette.primary.mainChannel} / 0.16)`,
    },
    '& .bar': {
      height: 9,
      width: 1,
      backgroundColor: 'currentColor',
      marginLeft: 1,
      marginRight: 1,
    },
  },
  '& .MuiSlider-track': {
    height: 1,
  },
  '& .MuiSlider-rail': {
    color: theme.vars.palette.grey[300],
    height: 2,
    opacity: 1,
  },
}));

const ThumbComponent = ({children, ...props}) => {
  return (
    <SliderThumb {...props}>
      {children}
      <span className="bar" />
      <span className="bar" />
      <span className="bar" />
    </SliderThumb>
  );
};

ThumbComponent.propTypes = {
  children: PropTypes.node,
};

const DEFAULT_VALUES = [0, 1];
const identityFn = x => x;

const SliderFilter = ({
  min = 0,
  max = 1,
  step = 1 / 100,
  values: valuesProp = DEFAULT_VALUES,
  unit,
  scale = identityFn,
  resetScale = identityFn,
  ...props
}) => {
  const [values, setValues] = useState(valuesProp);
  const [numberFields, setNumberFields] = useState(
    valuesProp.map(val => scale(val))
  );
  const {t} = useTranslation();

  useEffect(() => {
    setValues(valuesProp);
  }, [valuesProp]);

  useEffect(() => {
    setNumberFields(values.map(val => scale(val)));
  }, [values, scale]);

  const numberFieldProps = {
    min: scale(min),
    max: scale(max),
    unit,
    step: scale(step),
  };

  const onSliderChange = (_, newValues) => {
    setValues(newValues);
  };

  const onNumberFieldChange = index => event => {
    const {value} = event.target;
    const newValue = value ? Number(value) : '';

    const clonedValues = values.slice();
    const clonedNumberFields = numberFields.slice();
    clonedNumberFields[index] = newValue;

    if (typeof newValue === 'number') {
      const resetValue = resetScale(newValue);
      //cap values for sliders
      const cappedValue =
        resetValue < min ? min : resetValue > max ? max : resetValue;
      clonedValues[index] = cappedValue;
      setValues(clonedValues);
      props.onChangeComplete(clonedValues);
    }

    // keep what the user entered
    setNumberFields(clonedNumberFields);
  };

  const onNumberFieldBlur = index => event => {
    const {value} = event.target;
    const resetValue = resetScale(value);
    if (resetValue < min || resetValue > max) {
      setNumberFields(nf => {
        return index === 0 ? [min, nf[1]] : [nf[0], max];
      });
    } else if (value === '') {
      setNumberFields(values.map(val => scale(val)));
    }
  };

  return (
    <Box
      data-qe={`${props.dataQe}-sliderfilter`}
      sx={{
        width: '100%',
        py: 2,
      }}
    >
      <Box
        sx={{
          width: '100%',
          marginBottom: 1,
        }}
      >
        <Typography
          sx={{
            display: 'inline',
          }}
        >
          {t(props.name)}
        </Typography>
        <Typography
          variant="caption"
          sx={{
            display: 'inline',
            marginLeft: 1,
          }}
        >
          {`${scale(min)} - ${scale(max)}${t(unit)}`}
        </Typography>
      </Box>
      <Box
        sx={{
          marginTop: 1,
          display: 'flex',
          justifyContent: 'center',
        }}
      >
        <StyledSlider
          components={{Thumb: ThumbComponent}}
          min={min}
          max={max}
          onChange={onSliderChange}
          onChangeCommitted={(_, value) => {
            props.onChangeComplete(value);
          }}
          step={step}
          value={values.slice()}
        />
      </Box>
      <Box
        sx={{
          width: '100%',
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          marginTop: 1,
        }}
      >
        <NumberField
          {...numberFieldProps}
          value={numberFields[0]}
          onChange={onNumberFieldChange(0)}
          onBlur={onNumberFieldBlur(0)}
          data-qe="sliderfilter-min-input"
          pattern=""
        />
        <RemoveIcon sx={{margin: theme => theme.spacing(0, 1)}} />
        <NumberField
          {...numberFieldProps}
          value={numberFields[1]}
          onChange={onNumberFieldChange(1)}
          onBlur={onNumberFieldBlur(1)}
          data-qe="sliderfilter-max-input"
        />
      </Box>
    </Box>
  );
};

SliderFilter.propTypes = {
  min: PropTypes.number,
  max: PropTypes.number,
  step: PropTypes.number,
  values: PropTypes.array,
  unit: PropTypes.string,
  scale: PropTypes.func,
  resetScale: PropTypes.func,
  name: PropTypes.string,
  onChangeComplete: PropTypes.func,
  dataQe: PropTypes.string,
};

export default SliderFilter;
